import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { cn } from '@/lib/utils';

export interface PersonRowEmployee {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  avatarUrl?: string | null;
}

interface PersonRowProps {
  employee: PersonRowEmployee;
  /** Secondary line under the name (e.g. absence type + dates). */
  subline?: ReactNode;
  /** Optional trailing element (e.g. a date/status Badge). */
  trailing?: ReactNode;
  className?: string;
}

/**
 * A dashboard list row for a person. Cross-cutting rule: any row showing a person's name
 * is clickable → their profile. The name is formatted through the company-wide Name
 * Display setting via `useEmployeeNameFormatter`.
 */
export function PersonRow({ employee, subline, trailing, className }: PersonRowProps) {
  const formatName = useEmployeeNameFormatter();
  const initials =
    `${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <div className={cn('dashboard-row flex items-center gap-3 border-b border-border py-2.5 last:border-0', className)}>
      <Link
        to="/employees/$id"
        params={{ id: employee.id }}
        className="group flex min-w-0 flex-1 items-center gap-3"
      >
        <Avatar className="h-8 w-8">
          <AvatarImage src={employee.avatarUrl ?? undefined} />
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium group-hover:underline">
            {formatName({
              firstName: employee.firstName,
              lastName: employee.lastName,
              preferredName: employee.preferredName ?? undefined,
            })}
          </div>
          {subline != null && <div className="truncate text-xs text-muted-foreground">{subline}</div>}
        </div>
      </Link>
      {trailing != null && <div className="ml-auto shrink-0">{trailing}</div>}
    </div>
  );
}
